import React from 'react';
import { DatePicker } from 'antd';
import { dateRender, isDateInRange } from './DatePickerSystem.helper';
import PropTypes from 'prop-types';
import './DatePickerSystem.scss';

/**
 * Main DatePicker component with custom styling and disabled date functionality
 * @param {Object} props - Component props
 * @param {string} props.theme - Theme of the datepicker ('light' or 'dark')
 * @param {boolean} props.isError - Whether to show error state
 * @param {Date} props.minDate - Minimum selectable date
 * @param {Date} props.maxDate - Maximum selectable date
 * @param {string} props.availableDateTooltip - Tooltip text for dates that are available for selection
 * @param {string} props.unavailableDateTooltip - Tooltip text for dates that are not available for selection
 * @param {Object} props... - Additional props passed to Ant Design DatePicker
 */
const DatePickerSystem = ({
  theme = 'light',
  isError = false,
  minDate,
  maxDate,
  availableDateTooltip = '',
  unavailableDateTooltip = '',
  ...props
}) => {
  const isDateRangeRestricted = minDate || maxDate;

  if (!isDateRangeRestricted) {
    return (
      <DatePicker
        data-testid="datepicker-system"
        {...props}
        className={`datepicker-input-design-system theme--${theme} ${isError ? 'error' : ''}`}
        dropdownClassName={`datepicker-design-system theme--${theme}`}
      />
    );
  }

  return (
    <DatePicker
      {...props}
      data-testid="datepicker-system"
      className={`datepicker-input-design-system theme--${theme} ${isError ? 'error' : ''}`}
      dropdownClassName={`datepicker-design-system theme--${theme}`}
      dateRender={(date) =>
        dateRender({
          current: date,
          minDate: minDate,
          maxDate: maxDate,
          unavailableDateTooltip,
          availableDateTooltip
        })
      }
      disabledDate={(current) => {
        return !isDateInRange(current, minDate, maxDate);
      }}
    />
  );
};

DatePickerSystem.propTypes = {
  theme: PropTypes.oneOf(['light', 'dark']),
  isError: PropTypes.bool,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  availableDateTooltip: PropTypes.string,
  unavailableDateTooltip: PropTypes.string
};

export default DatePickerSystem;
